import { useRef, useReducer, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  Button,
  Form,
  FormControl,
  Input,
  Label,
  Loader,
  Select,
  CustomizedDatePicker,
  TextArea,
  CheckboxWithIcon
} from "@hydra/atom/components";
import { selectPermission } from "@/store/userSlice";
import { CustomActionDropdown } from "@/components/dynamic";

import { BoxedContent, Header, FormLeftHeader } from "@/components/common";
import {
  currencyRateFormReducer,
  initialState,
  setFormValue,
  setInitialState,
} from "@/reducers/finance/currencyRateFormReducer";
import {
  createCurrencyRate,
  updateCurrencyRate,
  getCurrencyRateById,
  deleteCurrencyRate
} from "@/api/finance/currencyRateApi";
import {
  getCurrencies
} from "@/api/finance/currencyApi";
import showToast from "@/utils/toast/helpers";
import { formatApiPayloadDate } from "@/utils/helpers";

const validationMessages = {
  required: {
    required: "This field is required",
  },
};

const prepareData = (state) => {
  const {
    currencyCode, baseCurrencyCode, effectiveDate, ...rest
  } = state;

  const data = {
    ...rest
  };

  data.currencyCode = currencyCode.value;
  data.baseCurrencyCode = baseCurrencyCode.value;
  data.effectiveDate = formatApiPayloadDate(effectiveDate);

  return data;
};

const prepareState = (data) => {
  const {
    currencyCode, baseCurrencyCode, effectiveDate, ...rest
  } = data;

  const state = {
    ...rest
  };

  state.currencyCode = {
    label: currencyCode,
    value: currencyCode
  };

  state.baseCurrencyCode = {
    label: baseCurrencyCode,
    value: baseCurrencyCode
  };

  state.effectiveDate = new Date(effectiveDate);

  return state;
};

function ActionCell({
  deleteMutation, id
}) {
  const actions = [
    {
      title: "Delete",
      onClick: () => { deleteMutation.mutate(id); },
      icon: "delete-icon"
    }
  ];
  return (
    <div className="action-cell">
      <CustomActionDropdown
        actions={actions}
        trigger={(
          <div className="action-dropdown-trigger">
            <span className="text">Actions</span>
            <span className="material-icons-outlined">expand_more</span>
          </div>
        )}
        testId="Add-GL-Action-Button"
      />
    </div>
  );
}

export default function AddCurrencyRate() {
  const [state, dispatch] = useReducer(currencyRateFormReducer, initialState);
  const deletePermission = useSelector(selectPermission({ parent: "Model", scope: "CurrencyRate", action: "Delete" }));

  const submitButtonRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isInitialLoading } = useQuery(
    ["currency-rate", id],
    () => getCurrencyRateById(id),
    {
      enabled: id !== undefined,
    }
  );

  useEffect(() => {
    if (id && data) {
      const stateData = prepareState(data?.data);
      dispatch(setInitialState(stateData));
    }
  }, [data]);

  const saveMutation = useMutation(createCurrencyRate, {
    onError: () => {
      showToast("Could not create. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Created successfully", "success");
      navigate(-1);
    },
  });

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData }) => updateCurrencyRate(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: () => {
        showToast("Updated successfully", "success");
        navigate(-1);
      },
    }
  );

  const deleteMutation = useMutation(deleteCurrencyRate, {
    onError: () => {
      showToast("Could not delete. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Deleted successfully", "success");
      QueryClient.invalidateQueries({
        queryKey: ["currency-rate"],
      });
    },
  });

  const handleCancel = () => {
    dispatch(setInitialState(initialState));
    navigate(-1);
  };

  const handleSave = () => {
    submitButtonRef.current.click();
  };

  const handleSubmit = async () => {
    const recordData = prepareData(state);

    if (id) {
      updateMutation.mutate({
        id,
        data: recordData,
      });
      return;
    }

    saveMutation.mutate(recordData);
  };

  if (id && isInitialLoading) {
    return <Loader />;
  }

  return (
    <BoxedContent className="gl-account-form">
      <Header
        showBreadcrumb
        leftContent={(
          <h1>{`${id ? "Edit" : "Create"} Currency`}</h1>
        )}
        // rightContent={(id && deletePermission) ? <ActionCell deleteMutation={deleteMutation} id={id} /> : null}
      />
      <Form
        onSubmit={handleSubmit}
        key="currency-form"
        className="dynamic-object-form"
        shouldScrollOnError
      >
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Add basic info"
              subtitle="Select currency, base currency, date and rate"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="currencyCode" label="Currency Code" />
                  <Select
                    name="currencyCode"
                    queryKey="currency"
                    placeholder="Select currency"
                    value={state.currencyCode}
                    onChange={(value) => dispatch(setFormValue("currencyCode", value))}
                    optionsLoader={() => getCurrencies()}
                    dataAccessKey="data"
                    optionLabelKey="code"
                    optionValueKey="code"
                    isClearable
                    testId="Currency-Code"
                    rules="required"
                    messages={validationMessages.required}
                  />
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="baseCurrencyCode" label="Base Currency Code" />
                  <Select
                    name="baseCurrencyCode"
                    queryKey="currency"
                    placeholder="Select currency"
                    value={state.baseCurrencyCode}
                    onChange={(value) => dispatch(setFormValue("baseCurrencyCode", value))}
                    optionsLoader={() => getCurrencies()}
                    dataAccessKey="data"
                    optionLabelKey="code"
                    optionValueKey="code"
                    isClearable
                    testId="Base-Currency-Code"
                    rules="required"
                    messages={validationMessages.required}
                  />
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="rate" label="Rate" />
                  <Input
                    type="number"
                    name="rate"
                    placeholder="Rate"
                    value={state.rate}
                    onChange={(value) => dispatch(setFormValue("rate", value))}
                    rules="required"
                    messages={validationMessages.required}
                    testId="Currency-Rate-Input"
                  />
                </FormControl>
              </div>
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="effectiveDate" label="Effective Date" />
                  <CustomizedDatePicker
                    name="effectiveDate"
                    value={state.effectiveDate}
                    onChange={(value) =>
                      dispatch(
                        setFormValue("effectiveDate", value || null)
                      )}
                    rules="required"
                    messages={validationMessages.required}
                    testId="Currency-Date-Input"
                  />
                </FormControl>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <FormLeftHeader
              title="Add notes"
              subtitle="Add notes"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="notes" label="Notes" />
                  <TextArea
                    type="text"
                    name="notes"
                    id="notes"
                    placeholder="Notes"
                    value={state.notes}
                    onChange={(value) => dispatch(setFormValue("notes", value))}
                  />
                </FormControl>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <FormLeftHeader
              title="Is currency rate active"
              subtitle="Enable or disable currency rate"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-3">
                <CheckboxWithIcon
                  id="isActive"
                  name="isActive"
                  label="Is Active"
                  value={state.isActive}
                  onChange={() =>
                    dispatch(
                      setFormValue("isActive", !state.isActive)
                    )}
                  testId="Currency-Active-Checkbox"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={{ display: "none" }}
            ref={submitButtonRef}
          >
            Save
          </button>
        </div>
      </Form>
      <div className="col-md-12">
        <div className="hr" />
      </div>

      <div className="col">
        <div className="buttons-at-end">
          <Button bordered small onClick={handleCancel} testId="Currency-Cancel-Button">
            Cancel
          </Button>
          <Button
            small
            className="save-btn"
            loading={saveMutation.isLoading || updateMutation.isLoading}
            onClick={handleSave}
            testId="Currency-Submit-Button"
          >
            Save
          </Button>
        </div>
      </div>
    </BoxedContent>
  );
}
